<template>
  <div class="site">

    <!-- NAV -->
    <nav class="nav">
      <div class="brand">
        <div class="brand-icon">
          <img src="./assets/ProfitLedgex.png" alt="ProfitLedgex logo">
        </div>
        <div class="brand-text">
          <span class="brand-name">ProfitLedgex</span>
          <span class="brand-tagline">Intelligent business insights with seamless invoicing.</span>
        </div>
      </div>

      <button
        class="nav-toggle"
        :class="{ 'nav-toggle--open': mobileNavOpen }"
        type="button"
        :aria-expanded="mobileNavOpen"
        aria-label="Toggle navigation"
        @click="mobileNavOpen = !mobileNavOpen"
      >
        <span />
        <span />
        <span />
      </button>

      <div class="nav-links" :class="{ 'nav-links--open': mobileNavOpen }">
        <a href="#features" @click="mobileNavOpen = false">Features</a>
        <a href="#invoicing" @click="mobileNavOpen = false">Invoicing</a>
        <a href="#pricing" @click="mobileNavOpen = false">Pricing</a>
        <a href="#contact" @click="mobileNavOpen = false">Support</a>
        <a href="https://profitledgex.com/login" target="_blank" rel="noopener" class="nav-login" @click="mobileNavOpen = false">Log in</a>
        <a href="https://profitledgex.com/signup" target="_blank" rel="noopener" class="btn btn-white btn-nav" @click="mobileNavOpen = false">Start Free Trial</a>
      </div>
    </nav>

    <!-- HERO -->
    <header class="hero">
      <div class="hero-content">
        <div class="hero-copy">
          <div class="pill">15-day free trial &middot; No card required</div>
          <h1>GST-ready invoicing and accounting for every Indian business</h1>
          <p>Invoices, customers, stock, and profit reports — in one simple app built for anyone who creates invoices frequently, from solo freelancers to large enterprises. No spreadsheets, no separate notebooks, nothing to calculate by hand.</p>
          <div class="hero-actions">
            <a href="https://profitledgex.com/signup" target="_blank" rel="noopener" class="btn btn-white btn-lg">Start Free Trial</a>
            <a href="#features" class="btn btn-outline btn-lg">See all features</a>
          </div>
          <p class="hero-note">Works as a mobile app or straight from your browser — install it like any other app.</p>

          <div class="hero-trust">
            <div class="hero-trust-item">
              <span class="hero-trust-icon">✓</span>
              Secure payments via Razorpay
            </div>
            <div class="hero-trust-item">
              <span class="hero-trust-icon">✓</span>
              English &amp; Hindi support
            </div>
            <div class="hero-trust-item">
              <span class="hero-trust-icon">✓</span>
              Your data, always deletable by you
            </div>
          </div>
        </div>

        <div class="hero-visual">
          <div class="mock-window">
            <div class="mock-titlebar">
              <span /><span /><span />
            </div>
            <img
              src="./assets/screenshots/dashboard.png"
              alt="ProfitLedgex dashboard showing income, expenses, profit, and low stock alerts"
              class="hero-screenshot"
            >
          </div>
        </div>
      </div>

      <svg viewBox="0 0 1200 60" class="hero-wave" preserveAspectRatio="none">
        <path d="M0,60 C300,0 900,0 1200,60 L1200,60 L0,60 Z" fill="#eaf4fb" />
      </svg>
    </header>

    <!-- FEATURE GRID -->
    <section id="features" class="section">
      <div class="section-head">
        <h2>Everything your business needs, in one app</h2>
        <p>From your first invoice to your monthly reports — ProfitLedgex handles it automatically.</p>
      </div>

      <div class="feature-grid">
        <div v-for="feature in features" :key="feature.title" class="feature-card">
          <div class="feature-icon" :class="feature.iconClass" />
          <div class="feature-title">{{ feature.title }}</div>
          <div class="feature-desc">{{ feature.desc }}</div>
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: Creating an invoice -->
    <section id="invoicing" class="deep-dive">
      <div class="deep-dive-inner">
        <div class="deep-dive-copy">
          <div class="badge badge-blue">INVOICE CREATION</div>
          <h3>Create an invoice online in under a minute</h3>
          <p>Your invoice number is auto-suggested. Pick a customer from a searchable list, or add a brand-new one right there on the invoice screen — no page reload, nothing lost. Add products the same way, with a live PDF preview building itself as you type, so you see exactly what your customer will receive before you save anything.</p>
          <ul class="check-list">
            <li>Auto-suggested, editable invoice numbers</li>
            <li>Add a new customer or product inline, mid-invoice</li>
            <li>Place of supply calculated automatically from GSTIN</li>
            <li>Live stock warnings if you're about to oversell</li>
          </ul>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/create-invoice-form.png"
            alt="ProfitLedgex invoice creation screen with customer, product, and stock hint"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: GST split -->
    <section class="deep-dive deep-dive--alt">
      <div class="deep-dive-inner deep-dive-inner--reverse">
        <div class="deep-dive-copy">
          <div class="badge badge-green">AUTOMATIC GST</div>
          <h3>Never calculate CGST, SGST, or IGST by hand again</h3>
          <p>Every time you generate an invoice, ProfitLedgex compares your business's GSTIN state code to your customer's. Same state, and it splits GST into CGST + SGST columns automatically. Different state, and it shows a combined IGST-style column instead — exactly as Indian tax law requires, with zero manual work.</p>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/invoice-gst-split.png"
            alt="A real ProfitLedgex invoice showing automatic CGST and SGST split on a two-laptop order"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: The PDF -->
    <section class="deep-dive">
      <div class="deep-dive-inner">
        <div class="deep-dive-copy">
          <div class="badge badge-blue">INVOICE PDF</div>
          <h3>A genuinely professional PDF, not a plain printout</h3>
          <p>Every invoice you generate is a fully branded, ready-to-send PDF — your business header and GSTIN, an itemized table with the correct GST split, your bank details pulled automatically from your Bank Account page, the total amount spelled out in words, your numbered Terms &amp; Conditions, and a signature block. Set your details once, and every future invoice looks this good automatically.</p>
        </div>
        <div class="deep-dive-visual deep-dive-visual--pdf">
          <img
            src="./assets/screenshots/invoice-pdf-sample.png"
            alt="A real ProfitLedgex generated invoice PDF with GST breakdown, bank details, amount in words, and terms & conditions"
            class="screenshot screenshot--pdf"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: WhatsApp -->
    <section class="deep-dive deep-dive--alt">
      <div class="deep-dive-inner deep-dive-inner--reverse">
        <div class="deep-dive-copy">
          <div class="badge badge-green">SHARE ON WHATSAPP</div>
          <h3>Share the real PDF from your phone, not just a link</h3>
          <p>Tap the WhatsApp icon on your phone and ProfitLedgex hands the actual invoice PDF straight to your share sheet — pick any contact, and they get the real file in the chat, just like sharing a photo. On a desktop browser, it opens WhatsApp with a ready-to-send download link instead, so sharing always works wherever you're working from.</p>
        </div>
        <div class="deep-dive-visual">
          <div class="mini-card mini-card--shadow whatsapp-mock">
            <div class="whatsapp-bubble">
              <span class="whatsapp-icon">📄</span>
              <div>
                <div class="whatsapp-file-name">Invoice-INV-0142.pdf</div>
                <div class="whatsapp-file-meta">PDF document · 142 KB</div>
              </div>
            </div>
            <div class="whatsapp-caption">Real file on mobile, instant link on desktop</div>
          </div>
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: All invoices in one place -->
    <section class="deep-dive">
      <div class="deep-dive-inner">
        <div class="deep-dive-copy">
          <div class="badge badge-blue">INVOICE TRACKING</div>
          <h3>Every invoice, every status, at a glance</h3>
          <p>See every invoice you've ever raised in one searchable, sortable list — Paid, Partially Paid, Pending, and Cancelled are all color-coded, and overdue unpaid invoices are automatically highlighted so nothing slips through the cracks.</p>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/invoices-list.png"
            alt="ProfitLedgex invoice list showing paid, partially paid, and pending invoices"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: Quotations -->
    <section class="deep-dive deep-dive--alt">
      <div class="deep-dive-inner deep-dive-inner--reverse">
        <div class="deep-dive-copy">
          <div class="badge badge-green">QUOTATIONS</div>
          <h3>Send an estimate first, convert it the moment they say yes</h3>
          <p>Not every sale starts with an invoice. Create a Quotation with the same easy form you already know — a branded PDF that doesn't touch your stock or your GST reports, since it isn't a real sale yet. When your customer confirms, open it and click "Convert to Invoice" — it becomes a genuine invoice with a fresh number, full GST classification, and full stock deduction, exactly as if you'd created it by hand.</p>
          <ul class="check-list">
            <li>Same fast form as invoicing, badged distinctly as a QUOTATION</li>
            <li>No stock reserved, no GST liability, until it converts</li>
            <li>One click turns an accepted quote into a real invoice</li>
            <li>Automatically flagged Expired if "Valid Until" passes unconverted</li>
          </ul>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/quotations-list.png"
            alt="ProfitLedgex quotations list showing draft, converted, and expired quotations"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: Dashboard -->
    <section class="deep-dive">
      <div class="deep-dive-inner">
        <div class="deep-dive-copy">
          <div class="badge badge-blue">DASHBOARD</div>
          <h3>Know how your business is doing, the moment you log in</h3>
          <p>Income, expenses, and profit as clear summary cards. A month-by-month income vs. expenses chart. Overdue invoices and low stock alerts surfaced automatically. Your Dashboard answers "how is my business doing today, and what needs my attention right now?" — without digging through menus.</p>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/dashboard.png"
            alt="ProfitLedgex dashboard with income vs expenses chart, overdue invoices, and low stock alerts"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- CUSTOMERS + PRODUCTS -->
    <section class="section section--tight">
      <div class="two-col">
        <div class="info-card">
          <div class="eyebrow eyebrow-green">CUSTOMERS</div>
          <h3>A complete customer directory</h3>
          <p>Store contact details, GSTIN, and full address for every customer. Search, sort, edit, or view a dedicated invoice history for anyone — with a shortcut to bill them again instantly.</p>
          <img
            src="./assets/screenshots/customers-list.png"
            alt="ProfitLedgex customer directory with contact details and GSTIN"
            class="info-screenshot"
          >
        </div>
        <div class="info-card">
          <div class="eyebrow eyebrow-blue">PRODUCTS &amp; INVENTORY</div>
          <h3>Optional stock tracking that runs itself</h3>
          <p>Toggle stock tracking per product. Every sale deducts automatically; every return or cancellation adds it back. Low stock triggers a warning badge, with a full, color-coded movement history tying every change back to the invoice that caused it.</p>
          <img
            src="./assets/screenshots/products-list.png"
            alt="ProfitLedgex product list with stock levels and a low stock warning"
            class="info-screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: Partial payments -->
    <section class="deep-dive deep-dive--alt">
      <div class="deep-dive-inner deep-dive-inner--reverse">
        <div class="deep-dive-copy">
          <div class="badge badge-green">PAYMENTS</div>
          <h3>Record full or partial payments, however your customers actually pay</h3>
          <p>Mark an invoice as paid and choose from Cash, Bank Transfer, UPI, Debit Card, Credit Card, Cheque, Net Banking, Wallet, or Other. Only got part of the payment? Enter that amount — ProfitLedgex automatically tracks the remaining balance due and updates the invoice status, so nothing is ever double-counted or forgotten.</p>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/invoice-view-partial.png"
            alt="A real ProfitLedgex invoice marked as partially paid, showing amount paid and balance due"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: Transactions ledger -->
    <section class="deep-dive">
      <div class="deep-dive-inner">
        <div class="deep-dive-copy">
          <div class="badge badge-blue">TRANSACTIONS</div>
          <h3>Every rupee in and out, in one ledger</h3>
          <p>The moment an invoice is marked paid — fully or partially — it lands in your transactions ledger automatically, no extra entry needed. You can also log manual income or expenses that aren't tied to an invoice, like rent, supplies, or cash sales, each with its own category, payment method, and note. It's the single source of truth your Dashboard and Reports are built from.</p>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/transactions-list.png"
            alt="ProfitLedgex transactions list showing income and expense entries with payment method and category"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- P&L REPORT -->
    <section class="deep-dive deep-dive--alt">
      <div class="deep-dive-inner">
        <div class="deep-dive-copy">
          <div class="badge badge-blue">P&amp;L REPORT</div>
          <h3>Your numbers, ready whenever you need them</h3>
          <p>Filter by month, year, or a custom date range to see income, expense, and net profit as clear summary cards, plus a complete transaction table. Export a branded PDF or a formatted Excel sheet in one click — ready for your accountant.</p>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/reports.png"
            alt="ProfitLedgex reports screen with income, expense, and net profit summary and export options"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: GST Reports / GSTR-1 -->
    <section class="deep-dive">
      <div class="deep-dive-inner deep-dive-inner--reverse">
        <div class="deep-dive-copy">
          <div class="badge badge-green">GST REPORTS</div>
          <h3>File your GSTR-1 without touching a spreadsheet</h3>
          <p>ProfitLedgex classifies every invoice as B2B, B2C Large, or B2C Small automatically — the moment you create or edit it, using the same GSTIN logic that already splits your CGST/SGST/IGST. Pick a month or GST quarter and get every GSTR-1 section ready to go: B2B and B2C invoices, an HSN-wise summary, your total tax liability, and Credit Notes (CDNR/CDNUR) — plus an ITC Summary tab covering the input tax credit on your vendor purchases, net of any Debit Notes, using that same period.</p>
          <ul class="check-list">
            <li>Automatic B2B / B2C Large / B2C Small classification</li>
            <li>HSN-wise summary with quantity, unit, and tax breakup</li>
            <li>Credit &amp; Debit Notes, filed straight into CDNR/CDNUR and ITC Reversed</li>
            <li>ITC Summary — eligible vs. blocked credit from your vendor bills</li>
            <li>Export to Excel — one sheet per section, ready for the GST portal</li>
            <li>Bundle every invoice PDF for the period into one zip download</li>
          </ul>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/gst-reports.png"
            alt="ProfitLedgex GSTR-1 report showing B2B invoices and tax liability summary"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: Vendors, Purchases & ITC -->
    <section class="deep-dive deep-dive--alt">
      <div class="deep-dive-inner">
        <div class="deep-dive-copy">
          <div class="badge badge-blue">PURCHASES &amp; ITC</div>
          <h3>Track what you spend as carefully as what you earn</h3>
          <p>Add your suppliers under Vendors, then record every bill you receive under Purchases — with the same line-item, HSN, and CGST/SGST/IGST detail your invoices already capture. Mark each line as ITC eligible or not, and ProfitLedgex rolls it all up into an ITC Summary right on the GST Reports page, using the same month or GST quarter you already file by.</p>
          <ul class="check-list">
            <li>Vendor directory with GSTIN format validation</li>
            <li>Itemized bills — quantity, HSN/SAC, rate, tax split per line</li>
            <li>Per-line ITC eligible / blocked flag, your call to make</li>
            <li>Issue Debit Notes for returns or corrections — netted straight into ITC Summary</li>
            <li>Mark bills as paid, linked straight into your Transactions ledger</li>
          </ul>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/purchase-itc-summary.png"
            alt="ProfitLedgex ITC summary showing eligible, blocked, and net input tax credit"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: Double-Entry Ledger -->
    <section class="deep-dive">
      <div class="deep-dive-inner">
        <div class="deep-dive-copy">
          <div class="badge badge-blue">DOUBLE-ENTRY LEDGER</div>
          <h3>A real double-entry ledger, running quietly underneath</h3>
          <p>Every invoice, purchase, and payment already posts itself into a proper Chart of Accounts — no journal entries to learn, nothing to enter by hand. Pull up a Trial Balance or Balance Sheet for any date and it's always balanced, drill into a General Ledger to see exactly what moved through any one account, or set your Opening Balances once if you're switching over from another system. It's there for you or your accountant to go as deep as you need, whenever you need it.</p>
          <ul class="check-list">
            <li>Chart of Accounts, auto-seeded — nothing to set up</li>
            <li>Trial Balance &amp; Balance Sheet, always reconciled to zero</li>
            <li>General Ledger — drill into any account, line by line</li>
            <li>Opening Balances for a clean start when you switch over</li>
            <li>Accrual P&amp;L, alongside your everyday cash-basis report</li>
          </ul>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/trial-balance.png"
            alt="ProfitLedgex Trial Balance showing every account balanced to zero"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: GSTR-2A/2B Reconciliation -->
    <section class="deep-dive deep-dive--alt">
      <div class="deep-dive-inner deep-dive-inner--reverse">
        <div class="deep-dive-copy">
          <div class="badge badge-green">2A/2B RECONCILIATION</div>
          <h3>Know before you file whether your ITC actually matches your vendors</h3>
          <p>Import your GSTR-2A or 2B straight from the GST portal and ProfitLedgex matches every row against what you've already recorded under Purchases — by GSTIN and invoice number, with a small tolerance for rounding and date differences. See exactly what's Matched, what's Mismatched, what's only in your books (credit at real risk of being disallowed), and what your vendor filed that you haven't claimed yet.</p>
          <ul class="check-list">
            <li>Import 2A or 2B directly as an Excel file</li>
            <li>Four clear buckets: Matched, Mismatched, Books-only, Return-only</li>
            <li>Malformed rows flagged, never silently skipped</li>
            <li>Full import history, one click to re-run any period</li>
          </ul>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/gst-reconciliation.png"
            alt="ProfitLedgex GSTR-2A/2B reconciliation showing matched, mismatched, and books-only buckets"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: Bank Reconciliation -->
    <section class="deep-dive">
      <div class="deep-dive-inner">
        <div class="deep-dive-copy">
          <div class="badge badge-blue">BANK RECONCILIATION</div>
          <h3>Match your books to your actual bank statement</h3>
          <p>Import a bank statement and ProfitLedgex automatically suggests a match for each line against a transaction you've already recorded — same amount, within a 3-day window. Confirm a suggestion, match one manually, or ignore lines like bank charges that were never going to match anything. Your Book Balance and Bank Balance sit side by side, so you always know exactly what's still outstanding.</p>
          <ul class="check-list">
            <li>Import a CSV or Excel bank statement</li>
            <li>Auto-matched suggestions, confirmed in one click</li>
            <li>Duplicate lines skipped automatically on re-import</li>
            <li>Never touches your Transactions — only the imported line</li>
          </ul>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/bank-reconciliation.png"
            alt="ProfitLedgex bank reconciliation showing imported statement lines matched to transactions"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: Fixed Assets -->
    <section class="deep-dive">
      <div class="deep-dive-inner">
        <div class="deep-dive-copy">
          <div class="badge badge-blue">FIXED ASSETS</div>
          <h3>Straight-line depreciation, posted for you every month</h3>
          <p>Add an asset — its cost, salvage value, and useful life — and it posts to your ledger immediately. Run depreciation whenever you like and ProfitLedgex catches up every month you've missed in one go, without ever posting the same month twice. Dispose of an asset and it simply stops depreciating going forward — its history stays exactly as it was.</p>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/fixed-assets-list.png"
            alt="ProfitLedgex fixed asset register showing cost, accumulated depreciation, and net book value"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: Payroll -->
    <section class="deep-dive deep-dive--alt">
      <div class="deep-dive-inner deep-dive-inner--reverse">
        <div class="deep-dive-copy">
          <div class="badge badge-green">PAYROLL</div>
          <h3>Gross-to-net salary posting, one balanced entry per run</h3>
          <p>Add your employees with their gross salary and PF/TDS/PT figures — the ones your CA already gives you, since ProfitLedgex doesn't calculate these by statutory slab. Run payroll for a month and every employee's net pay posts as one clean, balanced ledger entry. It's a bookkeeping tool, not a compliance filing service — you still handle challans and returns your usual way.</p>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/payroll-list.png"
            alt="ProfitLedgex payroll employee register showing gross salary, deductions, and net pay"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- WORKFLOW -->
    <section class="section">
      <div class="section-head">
        <h2>From signup to your first report, in four steps</h2>
      </div>
      <div class="steps-grid">
        <div v-for="step in steps" :key="step.title" class="step">
          <div class="step-number">{{ step.number }}</div>
          <div class="step-title">{{ step.title }}</div>
          <div class="step-desc">{{ step.desc }}</div>
        </div>
      </div>
    </section>

    <!-- SUBSCRIPTION -->
    <section class="section section--tight">
      <div class="two-col">
        <div class="info-card">
          <div class="eyebrow eyebrow-blue">FLEXIBLE PLANS</div>
          <h3>Pay for exactly the term you need</h3>
          <p>After your 15-day free trial, choose 1 month, 6 months, or 1 year — every plan includes every feature, and renewal is handled securely through Razorpay by card, UPI, netbanking, or wallet. Your subscription is extended the instant payment is confirmed, automatically.</p>
        </div>
        <div class="info-card">
          <div class="eyebrow eyebrow-green">NEVER LOCKED OUT</div>
          <h3>Renewing is always one click away</h3>
          <p>A friendly in-app reminder appears as your subscription nears expiry, with a one-click "Renew Now." And if your subscription does lapse, logging back in takes you straight to a renewal page — with an "Already paid? Check status" option if a payment ever gets interrupted, so you're never stuck or charged twice.</p>
        </div>
      </div>
    </section>

    <!-- DEEP DIVE: Team Members -->
    <section class="deep-dive">
      <div class="deep-dive-inner">
        <div class="deep-dive-copy">
          <div class="badge badge-blue">TEAM MEMBERS</div>
          <h3>Bring your accountant or staff on board — without sharing your password</h3>
          <p>Invite anyone by email and choose their access: Full Access, who can create, edit, and delete just like you, or View Only, who can see everything but change nothing. Everything they do lands in the exact same business records as your own — shared invoice numbering, shared customer lists, shared reports — with every entry quietly attributed to who actually created it. Business Profile, Bank Account, and Terms & Conditions always stay owner-only, no matter what access you grant.</p>
          <ul class="check-list">
            <li>Full Access or View Only, your choice per member</li>
            <li>Every entry attributed to who actually created it</li>
            <li>Owner-only: Business Profile, Bank Account, Terms &amp; Conditions</li>
            <li>Deactivate or remove anyone, any time</li>
          </ul>
        </div>
        <div class="deep-dive-visual">
          <img
            src="./assets/screenshots/team-members.png"
            alt="ProfitLedgex team members page showing active members and pending invitations with role"
            class="screenshot"
          >
        </div>
      </div>
    </section>

    <!-- ACCOUNT + SUPPORT strip -->
    <section class="section section--tight">
      <div class="three-col">
        <div class="mini-info-card">
          <div class="mini-info-title">Your account, your control</div>
          <div class="mini-info-desc">Update your photo, name, phone, username, and password whenever you like — with a self-service delete option so you're always in full control of your own data.</div>
        </div>
        <div class="mini-info-card">
          <div class="mini-info-title">Help &amp; guide, in your language</div>
          <div class="mini-info-desc">An in-app guide in English and Hindi walks through every feature, plus a full video walkthrough whenever you need it.</div>
        </div>
        <div class="mini-info-card">
          <div class="mini-info-title">Real support, when you need it</div>
          <div class="mini-info-desc">Tap-to-call, tap-to-email, WhatsApp, and a categorized ticketing system — real people, whenever something comes up.</div>
        </div>
      </div>
    </section>

    <!-- CONTACT -->
    <section id="contact" class="section section--tight contact-section">
      <div class="section-head">
        <h2>Talk to us before you sign up</h2>
        <p>Questions about GST invoicing, pricing, or whether ProfitLedgex fits your business? Reach us directly — no account needed.</p>
      </div>
      <div class="contact-grid">
        <a href="tel:+917483798592" class="contact-card">
          <div class="contact-card-label">Call</div>
          <div class="contact-card-value">+91 74837 98592</div>
        </a>
        <a href="tel:+917990198868" class="contact-card">
          <div class="contact-card-label">Call</div>
          <div class="contact-card-value">+91 79901 98868</div>
        </a>
        <a href="mailto:profitledgex@gmail.com" class="contact-card">
          <div class="contact-card-label">Email</div>
          <div class="contact-card-value">profitledgex@gmail.com</div>
        </a>
      </div>
    </section>

    <!-- WHY PROFITLEDGEX -->
    <section class="why">
      <div class="why-inner">
        <div class="section-head section-head--light">
          <h2>Built for how Indian businesses actually work</h2>
          <p>Not a generic accounting tool translated for India — GST, HSN codes, and rupee-first reporting from the ground up.</p>
        </div>
        <div class="why-grid">
          <div v-for="reason in whyReasons" :key="reason.title" class="why-card">
            <div class="why-title">{{ reason.title }}</div>
            <div class="why-desc">{{ reason.desc }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section id="faq" class="section">
      <div class="section-head">
        <h2>Frequently asked questions</h2>
        <p>Everything you need to know about creating invoices and managing your account with ProfitLedgex.</p>
      </div>
      <div class="faq-list">
        <details v-for="faq in faqs" :key="faq.q" class="faq-item">
          <summary class="faq-question">{{ faq.q }}</summary>
          <p class="faq-answer">{{ faq.a }}</p>
        </details>
      </div>
    </section>

    <!-- PRICING -->
    <section id="pricing" class="pricing">
      <div class="pricing-inner">
        <h2>Start free. Stay flexible.</h2>
        <p>Every plan includes every feature. After your 15-day free trial, choose the term that fits your business — and pay securely by card, UPI, netbanking, or wallet.</p>
        <div class="pricing-grid">
          <div v-for="plan in plans" :key="plan.name" class="pricing-card" :class="{ 'pricing-card--featured': plan.featured }">
            <div v-if="plan.featured" class="pricing-badge">MOST POPULAR</div>
            <div class="pricing-name">{{ plan.name }}</div>
            <div class="pricing-price">
              {{ plan.price }}
              <span class="pricing-period">{{ plan.period }}</span>
            </div>
            <div class="pricing-desc">{{ plan.desc }}</div>
          </div>
        </div>
        <a href="https://profitledgex.com/signup" target="_blank" rel="noopener" class="btn btn-white btn-lg pricing-cta">Start your free trial</a>
        <p class="pricing-footnote">No card required for your 15-day trial. Cancel anytime.</p>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="final-cta">
      <h2>Run your whole business from one app — free for 15 days</h2>
      <a href="https://profitledgex.com/signup" target="_blank" rel="noopener" class="btn btn-white btn-lg">Start Free Trial</a>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <div class="footer-top">
        <div class="footer-brand">
          <div class="brand">
            <div class="brand-icon brand-icon--small">
              <img src="./assets/ProfitLedgex.png" alt="ProfitLedgex logo">
            </div>
            <span class="brand-name brand-name--dark">ProfitLedgex</span>
          </div>
          <p class="footer-desc">GST-ready invoicing and accounting for any Indian business that invoices frequently — from freelancers to large enterprises.</p>
        </div>

        <div class="footer-cols">
          <div class="footer-col">
            <div class="footer-col-title">PRODUCT</div>
            <a href="#features">Features</a>
            <a href="#invoicing">Invoicing</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
            <a href="https://profitledgex.com/login" target="_blank" rel="noopener">Log in</a>
          </div>
          <div class="footer-col">
            <div class="footer-col-title">SUPPORT</div>
            <a href="#contact">Contact us</a>
            <a href="tel:+917483798592">Call +91 74837 98592</a>
            <a href="mailto:profitledgex@gmail.com">Email us</a>
          </div>
          <div class="footer-col">
            <div class="footer-col-title">LEGAL</div>
            <a href="https://profitledgex.com/terms" target="_blank" rel="noopener">Terms &amp; Conditions</a>
            <a href="https://profitledgex.com/privacy" target="_blank" rel="noopener">Privacy Policy</a>
          </div>
        </div>
      </div>
      <div class="footer-bottom">&copy; {{ currentYear }} ProfitLedgex. All rights reserved.</div>
    </footer>

  </div>
</template>

<script>
export default {
  name: 'ProfitLedgexWebsite',

  data() {
    return {
      mobileNavOpen: false,
      currentYear: new Date().getFullYear(),

      features: [
        {
          title: 'GST-ready invoicing',
          desc: "Generate a professional invoice PDF in under a minute. CGST/SGST or IGST is split automatically based on your customer's state — every time.",
          iconClass: 'feature-icon--blue',
        },
        {
          title: 'Quotations that convert',
          desc: 'Send a customer a branded price estimate with no GST or stock impact. One click converts it into a real invoice with a fresh number when they say yes.',
          iconClass: 'feature-icon--green',
        },
        {
          title: 'Customers & products',
          desc: 'A searchable directory of every customer and product. Add either one on the fly, right from the invoice screen, with no lost progress.',
          iconClass: 'feature-icon--blue',
        },
        {
          title: 'Stock tracking',
          desc: 'Turn on stock tracking for physical goods and every sale, return, or cancellation updates quantities automatically — with low-stock alerts.',
          iconClass: 'feature-icon--green',
        },
        {
          title: 'One-tap WhatsApp sharing',
          desc: 'Share the real invoice PDF straight from your phone, or a ready-to-send download link from desktop — whichever you\'re working from.',
          iconClass: 'feature-icon--blue',
        },
        {
          title: 'Partial payments',
          desc: 'Record part-payments across 9 payment methods and always know the exact balance still due.',
          iconClass: 'feature-icon--green',
        },
        {
          title: 'Income & expense ledger',
          desc: 'Paid invoices flow into your ledger automatically, and you can log manual income or expenses like rent and supplies alongside them.',
          iconClass: 'feature-icon--blue',
        },
        {
          title: 'P&L reports, ready to export',
          desc: 'Filter by month, year, or custom range and export a branded PDF report or a formatted Excel sheet — ready for your accountant.',
          iconClass: 'feature-icon--green',
        },
        {
          title: 'GSTR-1 filing, done for you',
          desc: 'Every invoice auto-classified as B2B, B2C Large, or B2C Small. Export a GSTR-1-ready Excel workbook or PDF in one click, month or quarter — Credit Notes included, plus an ITC Summary net of Debit Notes for your vendor purchases.',
          iconClass: 'feature-icon--blue',
        },
        {
          title: 'Vendors, purchases & ITC tracking',
          desc: 'Record vendor bills with the same line-item GST detail as your invoices, flag ITC-eligible lines, and see your input tax credit summarized right alongside GSTR-1.',
          iconClass: 'feature-icon--green',
        },
        {
          title: 'Double-entry ledger, automatically',
          desc: 'A full Chart of Accounts, Trial Balance, Balance Sheet, and General Ledger — posted for you from every invoice and purchase, always balanced.',
          iconClass: 'feature-icon--blue',
        },
        {
          title: 'GSTR-2A/2B reconciliation',
          desc: "Import your vendor-filed returns and see exactly what's matched, mismatched, or missing before you file — not after.",
          iconClass: 'feature-icon--green',
        },
        {
          title: 'Bank reconciliation',
          desc: 'Import a bank statement and get auto-matched suggestions against your recorded transactions, with your Book vs. Bank difference always in view.',
          iconClass: 'feature-icon--blue',
        },
        {
          title: 'Fixed assets & depreciation',
          desc: 'Track what your business owns and let straight-line depreciation post to your ledger every month, automatically.',
          iconClass: 'feature-icon--green',
        },
        {
          title: 'Payroll posting',
          desc: 'Add employees and their salary figures, and run payroll each month as one clean, balanced ledger entry.',
          iconClass: 'feature-icon--blue',
        },
        {
          title: 'Flexible subscription plans',
          desc: 'Pay monthly, half-yearly, or yearly — renew securely through Razorpay in a few taps, right inside the app.',
          iconClass: 'feature-icon--green',
        },
        {
          title: 'Team members with access control',
          desc: 'Invite your accountant or staff with Full Access or View Only. Business Profile, Bank Account, and Terms & Conditions always stay owner-only.',
          iconClass: 'feature-icon--blue',
        },
        {
          title: 'Help in your language',
          desc: 'A full in-app guide and demo video in English and Hindi, so nothing gets lost in translation.',
          iconClass: 'feature-icon--green',
        },
        {
          title: 'Works on any device',
          desc: 'Install ProfitLedgex like a native app on your phone or desktop. It stays automatically updated — no app store, no manual downloads.',
          iconClass: 'feature-icon--blue',
        },
      ],

      overdueSample: [
        { name: 'Ravi Traders', amount: '₹ 12,400' },
        { name: 'Meera Textiles', amount: '₹ 8,900' },
        { name: 'Anand Hardware', amount: '₹ 3,250' },
      ],

      steps: [
        {
          number: 1,
          title: 'Sign up free',
          desc: 'Create an account in under a minute and get 15 days of full access — no card required.',
        },
        {
          number: 2,
          title: 'Set up once',
          desc: 'Add your business profile, bank details, and terms & conditions — they auto-fill every invoice from here on.',
        },
        {
          number: 3,
          title: 'Bill your customers',
          desc: 'Create GST-compliant invoices and share them straight to WhatsApp as a real PDF file.',
        },
        {
          number: 4,
          title: 'Get paid & track profit',
          desc: 'Record full or partial payments, watch your ledger update itself, and export reports whenever you need them.',
        },
      ],

      whyReasons: [
        {
          title: 'GST built in, not bolted on',
          desc: 'CGST/SGST vs. IGST is decided automatically from real GSTIN state codes, and every invoice is classified for GSTR-1 the moment you create it.',
        },
        {
          title: 'No card to start',
          desc: 'A genuine 15-day free trial with full access to every feature. Pay only if you decide to keep going.',
        },
        {
          title: 'Nothing locked away',
          desc: 'Export your reports to PDF or Excel anytime, and delete your account and data yourself, whenever you choose.',
        },
        {
          title: 'Support that speaks your language',
          desc: 'In-app help, a demo video, and a real ticketing system — in English and Hindi.',
        },
      ],

      plans: [
        { name: '1 Month', price: '₹599', period: '/ month', desc: 'Pay monthly, cancel anytime', featured: false },
        { name: '6 Months', price: '₹3,499', period: '/ 6 months', desc: 'Best balance of savings and flexibility', featured: false },
        { name: '1 Year', price: '₹6,999', period: '/ year', desc: 'Maximum savings for committed teams', featured: true },
      ],

      faqs: [
        {
          q: 'How do I create an invoice online?',
          a: 'Log in to your ProfitLedgex account, click "Create Invoice," pick or add a customer, add your products or services, and generate the PDF — your invoice number, GST split, and totals are all calculated automatically. The whole process takes under a minute.',
        },
        {
          q: 'Does ProfitLedgex calculate GST automatically on invoices?',
          a: "Yes. ProfitLedgex compares your business's GSTIN state code to your customer's to automatically apply CGST + SGST for same-state sales, or a combined IGST-style column for inter-state sales — you never calculate GST by hand.",
        },
        {
          q: 'Does ProfitLedgex help with GSTR-1 filing?',
          a: 'Yes. The GST Reports section automatically classifies every invoice as B2B, B2C Large, or B2C Small, and gives you a ready-to-use B2B summary, B2C summary, HSN-wise summary, total tax liability, and Credit Notes (CDNR/CDNUR) for any month or GST quarter — exportable to Excel (one sheet per section) or PDF. A second tab, ITC Summary, covers eligible input tax credit from vendor purchases you record under Vendors and Purchases, net of any Debit Notes you\'ve issued. Together these cover outward supplies and ordinary domestic purchase ITC — reverse charge, imports, and ISD credit aren\'t included, and this isn\'t a full GSTR-3B computation, so always confirm final figures with your accountant before filing.',
        },
        {
          q: 'Can I track vendor bills and input tax credit (ITC)?',
          a: 'Yes. Add your suppliers under Vendors, then record each bill you receive under Purchases with the same itemized HSN and tax-split detail your invoices already have. Flag each line as ITC eligible or not, and the ITC Summary tab on GST Reports totals your eligible and blocked credit — plus an HSN-wise breakdown — for the same period you\'re filing GSTR-1 for. Need to correct a bill or return goods to a vendor? Issue a Debit Note directly against the purchase, and it\'s automatically netted into your ITC Summary as ITC Reversed.',
        },
        {
          q: 'Does ProfitLedgex do real double-entry accounting, not just invoicing?',
          a: "Yes. Underneath the invoices and purchases, ProfitLedgex keeps a genuine double-entry ledger — a Chart of Accounts is set up for you automatically, and every invoice, purchase, and payment posts itself. You get a Trial Balance and Balance Sheet that are always reconciled, a General Ledger to drill into any single account, and an Accrual P&L alongside your everyday cash-basis report. If you're switching over from another system, Opening Balances lets you set your starting figures once.",
        },
        {
          q: 'Can I reconcile my GST return against my books before filing?',
          a: 'Yes. Import your GSTR-2A or GSTR-2B Excel export from the GST portal and ProfitLedgex matches it against your recorded Purchases by GSTIN and invoice number. You get four clear buckets — Matched, Mismatched, credit only in your books (at risk of being disallowed), and credit your vendor filed that you haven\'t claimed yet — so you know before you file, not after. It covers B2B and CDNR rows only; amendments, ISD credit, and imports aren\'t included.',
        },
        {
          q: 'Can I reconcile my bank statement against what I\'ve recorded?',
          a: 'Yes. Import a CSV or Excel bank statement and ProfitLedgex automatically suggests matches against your recorded transactions — same amount, within a 3-day window. Confirm, match manually, or ignore lines like bank charges that were never going to match anything. It never edits your Transactions or ledger entries themselves, only the imported statement line.',
        },
        {
          q: 'Does ProfitLedgex handle fixed assets and payroll?',
          a: "Yes, at a bookkeeping level. Fixed Assets tracks what your business owns and posts straight-line depreciation to your ledger every month automatically, catching up any months you've missed. Payroll lets you add employees with their salary and PF/TDS/PT figures — the ones your CA already gives you — and posts one balanced ledger entry each time you run it. Neither is a compliance or filing service: PF/TDS/PT amounts are entered by you, not computed by statutory slab, and you still handle challans and returns your usual way.",
        },
        {
          q: 'Can I send a customer a price estimate before invoicing them?',
          a: 'Yes. Create a Quotation the same way you\'d create an invoice — it doesn\'t affect your stock or GST reports, since it isn\'t a real sale yet. Once your customer confirms, open it and click "Convert to Invoice" — it becomes a genuine invoice with a fresh number, full GST classification, and full stock deduction, exactly as if you\'d created it by hand. A quotation can only ever be converted once.',
        },
        {
          q: 'Can more than one person work on my ProfitLedgex account?',
          a: 'Yes. Invite your accountant or staff from the Team Members page with Full Access (can create, edit, and delete just like you) or View Only (can see everything, change nothing). Everything they do lands in your same shared business records, and ProfitLedgex keeps track of who created what. Business Profile, Bank Account, and Terms & Conditions always stay owner-only, and a team member never needs a subscription of their own — their access simply follows yours.',
        },
        {
          q: 'Do I need to create an account to use ProfitLedgex?',
          a: 'Yes, you need a free ProfitLedgex account to create and manage invoices. Signing up takes under a minute and starts your 15-day free trial immediately, with no card required.',
        },
        {
          q: 'Can I manage customers and products, not just invoices?',
          a: 'Yes. ProfitLedgex includes a full customer directory, a product catalog with optional stock tracking, an income and expense ledger, and reports — everything a growing business needs alongside invoicing, in one account.',
        },
        {
          q: 'Is there a free trial before I have to pay?',
          a: 'Yes — every new account gets a genuine 15-day free trial with full access to every feature, no credit card required. After the trial, you can choose a 1 month, 6 month, or 1 year plan.',
        },
        {
          q: 'Can I delete my account and data?',
          a: 'Yes. You can delete your ProfitLedgex account and all associated data yourself at any time from your account settings — no need to contact support.',
        },
      ],
    };
  },
};
</script>

<style scoped>
.site {
  width: 100%;
}

/* ═══════════════ NAV + HERO ═══════════════ */
.hero {
  background: linear-gradient(150deg, #052f5e 0%, #0a5a90 35%, #0d8478 68%, #15a355 100%);
  position: relative;
}

.nav {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 48px;
  position: sticky;
  top: 0;
  z-index: 20;
  background: linear-gradient(150deg, #052f5e 0%, #0a5a90 35%, #0d8478 68%, #15a355 100%);
  border-bottom: 1px solid rgba(255, 255, 255, .16);
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .15);
  flex-shrink: 0;
}

.brand-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-icon--small {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  box-shadow: none;
}

.brand-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.brand-name {
  font-family: 'Manrope', sans-serif;
  font-weight: 800;
  font-size: 20px;
  color: #fff;
  line-height: 1.1;
}

.brand-tagline {
  font-size: 11.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, .7);
  line-height: 1.2;
}

.brand-name--dark {
  color: #0A2540;
  font-size: 16px;
}

.nav-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 34px;
  height: 34px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.nav-toggle span {
  display: block;
  height: 2px;
  width: 100%;
  background: #fff;
  border-radius: 2px;
  transform-origin: center;
  transition: transform .2s ease, opacity .2s ease;
}

.nav-toggle--open span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.nav-toggle--open span:nth-child(2) {
  opacity: 0;
}

.nav-toggle--open span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 28px;
}

.nav-links a:not(.btn) {
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  opacity: .92;
}

.nav-login {
  font-weight: 600 !important;
}

.btn-nav {
  padding: 11px 20px;
  font-size: 14px;
}

.hero-content {
  max-width: 1440px;
  margin: 0 auto;
  padding: 56px 32px 0;
  display: flex;
  align-items: center;
  gap: 56px;
  position: relative;
  z-index: 2;
}

.hero-copy {
  flex: 1;
  max-width: 540px;
}

.pill {
  display: inline-block;
  background: rgba(255, 255, 255, .16);
  border: 1px solid rgba(255, 255, 255, .3);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 20px;
  margin-bottom: 22px;
}

.hero-copy h1 {
  font-family: 'Manrope', sans-serif;
  font-weight: 800;
  font-size: 48px;
  line-height: 1.15;
  margin: 0;
  color: #fff;
}

.hero-copy p {
  font-size: 17px;
  line-height: 1.6;
  color: rgba(255, 255, 255, .9);
  margin: 20px 0 0;
}

.hero-actions {
  display: flex;
  gap: 14px;
  margin-top: 32px;
  flex-wrap: wrap;
}

.hero-note {
  font-size: 13px;
  color: rgba(255, 255, 255, .75) !important;
  margin-top: 18px !important;
}

.hero-trust {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
  margin-top: 26px;
  padding-top: 22px;
  border-top: 1px solid rgba(255, 255, 255, .18);
}

.hero-trust-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: rgba(255, 255, 255, .85);
  font-weight: 500;
}

.hero-trust-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .18);
  font-size: 10px;
  flex-shrink: 0;
}

.hero-visual {
  flex: 1.1;
  min-width: 0;
}

.mock-window {
  background: #fff;
  border-radius: 14px 14px 0 0;
  box-shadow: 0 30px 70px rgba(10, 37, 64, .35);
  overflow: hidden;
}

.mock-titlebar {
  background: #f5f6f8;
  padding: 10px 14px;
  display: flex;
  gap: 6px;
  align-items: center;
}

.mock-titlebar span {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #e0e3e8;
}

.mock-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .06em;
  color: #8a93a1;
  margin-bottom: 12px;
}

.hero-screenshot {
  display: block;
  width: 100%;
  height: auto;
}

.hero-wave {
  display: block;
  width: 100%;
  height: 60px;
  position: relative;
  z-index: 1;
}

/* ═══════════════ SECTIONS ═══════════════ */
.section {
  max-width: 1440px;
  margin: 0 auto;
  padding: 20px 32px 80px;
  scroll-margin-top: 20px;
}

.section--tight {
  padding: 0 32px 80px;
}

.section-head {
  text-align: center;
  max-width: 860px;
  margin: 0 auto 52px;
}

.section-head h2 {
  font-family: 'Manrope', sans-serif;
  font-weight: 800;
  font-size: 34px;
  margin: 0;
  color: #0A2540;
}

.section-head p {
  font-size: 16px;
  color: #5b6472;
  margin-top: 12px;
  line-height: 1.6;
}

/* ═══════════════ FAQ ═══════════════ */
.faq-list {
  max-width: 760px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.faq-item {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 12px;
  padding: 18px 22px;
}

.faq-question {
  cursor: pointer;
  font-weight: 700;
  font-size: 15.5px;
  color: #0A2540;
  list-style: none;
}

.faq-question::-webkit-details-marker {
  display: none;
}

.faq-question::after {
  content: '+';
  float: right;
  font-weight: 700;
  color: #0a6cc0;
}

.faq-item[open] .faq-question::after {
  content: '−';
}

.faq-answer {
  font-size: 14.5px;
  color: #5b6472;
  line-height: 1.7;
  margin: 14px 0 0;
}

.section-head--light h2 {
  color: #fff;
}

.section-head--light p {
  color: rgba(255, 255, 255, .75);
}

/* ═══════════════ FEATURE GRID ═══════════════ */
.feature-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.feature-card {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 14px;
  padding: 28px;
}

.feature-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  margin-bottom: 16px;
}

.feature-icon--blue { background: #e8f0ff; }
.feature-icon--green { background: #e6f7ee; }

.feature-title {
  font-weight: 700;
  font-size: 17px;
  margin-bottom: 8px;
  color: #0A2540;
}

.feature-desc {
  font-size: 14px;
  color: #5b6472;
  line-height: 1.6;
}

/* ═══════════════ DEEP DIVE ═══════════════ */
.deep-dive {
  background: #fff;
  padding: 80px 32px;
  scroll-margin-top: 20px;
}

.deep-dive--alt {
  background: linear-gradient(160deg, #e8f2fc 0%, #f2f8fd 45%, #eaf4fb 100%);
}

.deep-dive-inner {
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 64px;
}

.deep-dive-inner--reverse {
  flex-direction: row-reverse;
}

.deep-dive-copy {
  flex: 1;
}

.badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 20px;
  margin-bottom: 16px;
}

.badge-blue { background: #e8f0ff; color: #0a6cc0; }
.badge-green { background: #e6f7ee; color: #15a355; }

.deep-dive-copy h3 {
  font-family: 'Manrope', sans-serif;
  font-weight: 800;
  font-size: 28px;
  margin: 0;
  line-height: 1.25;
  color: #0A2540;
}

.deep-dive-copy p {
  font-size: 15px;
  color: #5b6472;
  line-height: 1.7;
  margin-top: 16px;
}

.check-list {
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.check-list li {
  font-size: 14px;
  color: #374955;
  padding-left: 26px;
  position: relative;
  line-height: 1.5;
}

.check-list li::before {
  content: '✓';
  position: absolute;
  left: 0;
  top: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e6f7ee;
  color: #15a355;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.deep-dive-visual {
  flex: 1;
}

.screenshot {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 12px;
  border: 1px solid #e4e7ec;
  box-shadow: 0 20px 40px rgba(10, 37, 64, .12);
}

.deep-dive-visual--pdf {
  display: flex;
  justify-content: center;
}

.screenshot--pdf {
  width: 100%;
  max-width: 380px;
}

.mini-card {
  background: #f7f8fa;
  border: 1px solid #eef0f3;
  border-radius: 12px;
  padding: 20px;
}

.mini-card--shadow {
  background: #fff;
  box-shadow: 0 20px 40px rgba(10, 37, 64, .08);
}

.mini-table-head {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 8px;
  font-size: 11px;
  color: #8a93a1;
  font-weight: 700;
  border-bottom: 1px solid #e4e7ec;
  padding-bottom: 8px;
}

.mini-table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 8px;
  font-size: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eef0f3;
  color: #0A2540;
}

.gst-mock-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1.5px solid #e4e7ec;
  font-size: 13px;
  font-weight: 700;
  color: #0A2540;
}

.gst-mock-total span:last-child {
  color: #15a355;
}

/* 2A/2B reconciliation mockup — a 4-tile bucket summary instead of a
   table, since "Matched / Mismatched / Books-only / Return-only" reads
   better as stat tiles than table rows. Same rounded/color language as
   the rest of the site's mockups (mini-table hairlines, badge colors). */
.recon-mock {
  padding-bottom: 16px;
}

.recon-tiles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 14px;
}

.recon-tile {
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.recon-tile-num {
  font-family: 'Manrope', sans-serif;
  font-weight: 800;
  font-size: 22px;
}

.recon-tile-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .03em;
}

.recon-tile--good { background: #e6f7ee; }
.recon-tile--good .recon-tile-num { color: #15a355; }
.recon-tile--good .recon-tile-label { color: #158a49; }

.recon-tile--warn { background: #fff4e5; }
.recon-tile--warn .recon-tile-num { color: #b8720a; }
.recon-tile--warn .recon-tile-label { color: #b8720a; }

.recon-tile--bad { background: #fdeaea; }
.recon-tile--bad .recon-tile-num { color: #c0392b; }
.recon-tile--bad .recon-tile-label { color: #c0392b; }

.recon-tile--neutral { background: #e8f0ff; }
.recon-tile--neutral .recon-tile-num { color: #0a6cc0; }
.recon-tile--neutral .recon-tile-label { color: #0a6cc0; }

.whatsapp-mock {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 28px 20px;
}

.whatsapp-bubble {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #dcf8c6;
  border-radius: 12px 12px 12px 2px;
  padding: 14px 16px;
  max-width: 260px;
}

.whatsapp-icon {
  font-size: 26px;
  flex-shrink: 0;
}

.whatsapp-file-name {
  font-size: 13px;
  font-weight: 700;
  color: #0A2540;
}

.whatsapp-file-meta {
  font-size: 11px;
  color: #5b6472;
  margin-top: 2px;
}

.whatsapp-caption {
  font-size: 12px;
  color: #15a355;
  font-weight: 700;
}

/* ═══════════════ TWO / THREE COL INFO ═══════════════ */
.two-col {
  max-width: 1440px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

.info-card {
  background: #eaf4fb;
  border: 1px solid #eef0f3;
  border-radius: 14px;
  padding: 32px;
}

.eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .05em;
  margin-bottom: 10px;
}

.eyebrow-green { color: #15a355; }
.eyebrow-blue { color: #0a6cc0; }

.info-card h3 {
  font-family: 'Manrope', sans-serif;
  font-weight: 800;
  font-size: 21px;
  margin: 0 0 12px;
  color: #0A2540;
}

.info-card p {
  font-size: 14px;
  color: #5b6472;
  line-height: 1.7;
  margin: 0;
}

.info-screenshot {
  display: block;
  width: 100%;
  height: auto;
  margin-top: 20px;
  border-radius: 10px;
  border: 1px solid #e4e7ec;
  box-shadow: 0 12px 28px rgba(10, 37, 64, .10);
}

.three-col {
  max-width: 1440px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.mini-info-card {
  border: 1px solid #eef0f3;
  border-radius: 14px;
  padding: 28px;
  background: #fff;
}

.mini-info-title {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 10px;
  color: #0A2540;
}

.mini-info-desc {
  font-size: 14px;
  color: #5b6472;
  line-height: 1.7;
}

/* ═══════════════ CONTACT ═══════════════ */
.contact-section {
  max-width: 1440px;
  margin: 0 auto;
}

.contact-grid {
  max-width: 1440px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.contact-card {
  display: block;
  border: 1px solid #eef0f3;
  border-radius: 14px;
  padding: 24px 28px;
  background: #fff;
  text-decoration: none;
  transition: box-shadow .15s ease, border-color .15s ease, transform .15s ease;
}

.contact-card:hover {
  border-color: #0a6cc0;
  box-shadow: 0 12px 28px rgba(10, 37, 64, .10);
  transform: translateY(-2px);
}

.contact-card-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: #0a6cc0;
  margin-bottom: 8px;
}

.contact-card-value {
  font-size: 18px;
  font-weight: 700;
  color: #0A2540;
}

/* ═══════════════ WORKFLOW STEPS ═══════════════ */
.steps-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.step-number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #0a6cc0;
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.step-title {
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 6px;
  color: #0A2540;
}

.step-desc {
  font-size: 13.5px;
  color: #5b6472;
  line-height: 1.6;
}

/* ═══════════════ WHY PROFITLEDGEX ═══════════════ */
.why {
  background: linear-gradient(150deg, #052f5e 0%, #0a5a90 35%, #0d8478 68%, #15a355 100%);
  padding: 80px 32px;
}

.why-inner {
  max-width: 1440px;
  margin: 0 auto;
}

.why-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.why-card {
  background: rgba(255, 255, 255, .08);
  border: 1px solid rgba(255, 255, 255, .16);
  border-radius: 14px;
  padding: 24px;
}

.why-title {
  font-weight: 700;
  font-size: 15.5px;
  color: #fff;
  margin-bottom: 8px;
}

.why-desc {
  font-size: 13.5px;
  color: rgba(255, 255, 255, .78);
  line-height: 1.6;
}

/* ═══════════════ PRICING ═══════════════ */
.pricing {
  background: #0A2540;
  padding: 80px 32px;
  scroll-margin-top: 20px;
}

.pricing-inner {
  max-width: 1440px;
  margin: 0 auto;
  text-align: center;
}

.pricing-inner h2 {
  font-family: 'Manrope', sans-serif;
  font-weight: 800;
  font-size: 32px;
  margin: 0;
  color: #fff;
}

.pricing-inner > p {
  font-size: 15.5px;
  color: rgba(255, 255, 255, .75);
  margin-top: 12px;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 44px;
  max-width: 840px;
  margin-left: auto;
  margin-right: auto;
}

.pricing-card {
  background: rgba(255, 255, 255, .06);
  border: 1px solid rgba(255, 255, 255, .12);
  border-radius: 14px;
  padding: 32px 24px;
  position: relative;
}

.pricing-card--featured {
  background: rgba(255, 255, 255, .1);
  border: 1.5px solid #15a355;
}

.pricing-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #15a355;
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 20px;
  white-space: nowrap;
}

.pricing-name {
  font-weight: 700;
  font-size: 16px;
  color: #fff;
}

.pricing-price {
  font-family: 'Manrope', sans-serif;
  font-weight: 800;
  font-size: 30px;
  color: #fff;
  margin-top: 14px;
}

.pricing-period {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 13px;
  color: rgba(255, 255, 255, .55);
}

.pricing-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, .65);
  margin-top: 8px;
}

.pricing-cta {
  margin-top: 36px;
}

.pricing-footnote {
  font-size: 12.5px !important;
  color: rgba(255, 255, 255, .55) !important;
  margin-top: 14px !important;
  max-width: none !important;
}

/* ═══════════════ FINAL CTA ═══════════════ */
.final-cta {
  background: linear-gradient(150deg, #052f5e 0%, #0a5a90 35%, #0d8478 68%, #15a355 100%);
  padding: 70px 32px;
  text-align: center;
  scroll-margin-top: 20px;
}

.final-cta h2 {
  font-family: 'Manrope', sans-serif;
  font-weight: 800;
  font-size: 30px;
  margin: 0 0 26px;
  color: #fff;
}

/* ═══════════════ FOOTER ═══════════════ */
.footer {
  background: #fff;
  border-top: 1px solid #eef0f3;
  padding: 48px 48px 32px;
  scroll-margin-top: 20px;
}

.footer-top {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 40px;
  flex-wrap: wrap;
}

.footer-brand {
  max-width: 280px;
}

.footer-desc {
  font-size: 13px;
  color: #5b6472;
  line-height: 1.6;
  margin-top: 14px;
}

.footer-cols {
  display: flex;
  gap: 64px;
  flex-wrap: wrap;
}

.footer-col-title {
  font-size: 12px;
  font-weight: 700;
  color: #8a93a1;
  letter-spacing: .05em;
  margin-bottom: 14px;
}

.footer-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.footer-col a {
  font-size: 13.5px;
  color: #0A2540;
}

.footer-bottom {
  width: 100%;
  margin: 32px 0 0;
  padding-top: 24px;
  border-top: 1px solid #eef0f3;
  font-size: 12.5px;
  color: #8a93a1;
}

/* ═══════════════ SHARED BUTTONS ═══════════════ */
.btn {
  display: inline-block;
  font-weight: 700;
  border-radius: 10px;
  text-align: center;
}

.btn-white {
  background: #fff;
  color: #0a6cc0;
  box-shadow: 0 8px 20px rgba(0, 0, 0, .15);
}

.btn-outline {
  border: 1.5px solid rgba(255, 255, 255, .5);
  color: #fff;
}

.btn-lg {
  font-size: 16px;
  padding: 15px 26px;
}

/* ═══════════════ RESPONSIVE ═══════════════ */
@media (max-width: 960px) {
  .brand-tagline {
    display: none;
  }

  .nav-toggle {
    display: flex;
  }

  .nav-links {
    display: none;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 18px 0 4px;
    order: 3;
  }

  .nav-links--open {
    display: flex;
  }

  .nav {
    flex-wrap: wrap;
    padding: 16px 24px;
  }

  .btn-nav {
    width: 100%;
  }

  .hero-content {
    flex-direction: column;
    align-items: stretch;
  }

  .hero-copy {
    max-width: none;
  }

  .hero-copy h1 {
    font-size: 38px;
  }

  .feature-grid,
  .three-col,
  .contact-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .steps-grid,
  .why-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .two-col {
    grid-template-columns: 1fr;
  }

  .deep-dive-inner,
  .deep-dive-inner--reverse {
    flex-direction: column;
  }

  .pricing-grid {
    grid-template-columns: 1fr;
    max-width: 360px;
  }
}

@media (max-width: 680px) {
  .nav {
    padding: 18px 20px;
  }

  .hero-content {
    padding: 40px 20px 0;
  }

  .hero-copy h1 {
    font-size: 30px;
  }

  .hero-copy p {
    font-size: 15.5px;
  }

  .hero-actions {
    flex-direction: column;
  }

  .hero-actions .btn {
    width: 100%;
  }

  .hero-trust {
    flex-direction: column;
    gap: 10px;
  }

  .section,
  .section--tight,
  .deep-dive,
  .why,
  .pricing,
  .final-cta,
  .footer {
    padding-left: 20px;
    padding-right: 20px;
  }

  .section-head h2,
  .final-cta h2,
  .pricing-inner h2 {
    font-size: 26px;
  }

  .feature-grid,
  .three-col,
  .contact-grid,
  .steps-grid,
  .why-grid {
    grid-template-columns: 1fr;
  }

  .footer-top {
    flex-direction: column;
  }

  .footer-cols {
    gap: 40px;
  }
}
</style>

<style>
*,
*::before,
*::after {
  box-sizing: border-box;
}

html {
  width: 100%;
  overflow-x: hidden;
}

body {
  width: 100%;
  margin: 0;
  background: linear-gradient(160deg, #e8f2fc 0%, #f2f8fd 45%, #eaf4fb 100%);
  font-family: 'Inter', system-ui, sans-serif;
  color: #0A2540;
  -webkit-font-smoothing: antialiased;
}

a {
  color: #0a6cc0;
  text-decoration: none;
}

a:hover {
  color: #15a355;
}

::selection {
  background: #0a6cc0;
  color: #fff;
}
</style>
